<template>
  <div class="sponsorship-form-edit">
    <form @submit.prevent="submit">
      <magic-container>
        <v-text-field
          v-model="name"
          label="Logo Sponsorship title"
          required
          type="text"
          :error-messages="nameErrors"
          :disabled="!canUserEditFields"
          @blur="v$.name.$touch()"
        />
        <platform-select
          :is-multiple="false"
          :error-messages="countryErrors"
          :disabled="!canUserEditFields"
          v-model="country"
        />
      </magic-container>

      <layout-section-header
        title="Business details"
        :has-trigger="false"
      />

      <magic-container>
        <div>
          <v-autocomplete
            v-model="businessId"
            :items="businesses"
            label="Business"
            required
            clearable
            item-title="name"
            item-value="id"
            prepend-inner-icon="store"
            :disabled="!canUserEditFields"
            :hide-details="false"
            :loading="isLoadingBusinesses"
            v-model:search="businessSearch"
            :error-messages="businessIdErrors"
            @update:model-value="isSelectingBusiness = true"
          />
          <dropdowns-merchant
            v-model="merchant"
            :preselected-merchants="[merchant]"
            :hide-details="false"
            :countries="[country]"
            :error-messages="merchantErrors"
            is-single-country
            :disabled="!canUserEditFields"
            @focus="v$.country.$touch()"
          />
        </div>

        <span>
          <FileUploadArea
            ref="uploadArea"
            :on-upload-file="onUploadFile"
            :can-update-file="canUserEditFields"
          />
        </span>
      </magic-container>

      <alerts-base
        v-if="isTryingSubmit && !isImageSelected && !isDefaultMerchantImageSet"
        type="error"
        class="mb-2"
      >
        <template v-slot:text>
          Logo image is required
        </template>
      </alerts-base>

      <layout-section-header
        title="Logo Sponsorship Details"
        :has-trigger="false"
      />

      <magic-container>
        <div>
          <base-date-picker
            :date="startDate"
            :config="dateConfig"
            :hide-details="false"
            :error-messages="startDateErrors"
            :on-change="onChangeStartDate"
            placeholder="Start date"
            :is-disabled="!canUserEditFields"
            :has-clear="false"
          />

          <div class="time-fields">
            <v-select
              v-model="startTime"
              :items="hours"
              :hide-details="false"
              :label="$t('start_time')"
              :error-messages="startTimeErrors"
              :on-blur="v$.startTime.$touch"
              :disabled="!canUserEditFields"
              required
            />
            <v-select
              v-model="endTime"
              :items="hours"
              :label="$t('end_time')"
              :hide-details="false"
            />
          </div>
        </div>

        <div>
          <base-date-picker
            :date="endDate"
            :config="endDateConfig"
            :hide-details="false"
            :error-messages="endDateErrors"
            :on-change="onChangeEndDate"
            placeholder="End date"
            :has-clear="false"
          />

          <v-autocomplete
            clearable
            v-model="status"
            :items="statuses"
            prepend-inner-icon="download_done"
            :hide-details="false"
            item-title="name"
            item-value="id"
            label="Status"
            :disabled="canUserEditFields"
          />
        </div>
      </magic-container>

      <layout-section-header
        title="Category Page"
        :has-trigger="false"
      />

      <div>
        <v-autocomplete
          clearable
          v-model="selectedCategories"
          :items="categories"
          prepend-inner-icon="category"
          :hide-details="false"
          :loading="isLoadingCategory"
          :disabled="!canUserEditFields"
          multiple
          chips
          closable-chips
          :return-object="true"
          :error-messages="selectedCategoriesErrors"
          @update:search="categorySearch"
          @click="v$.country.$touch()"
          item-title="name"
          label="Category"
        >
          <template #chip="{ item, props }">
            <v-chip
              v-bind="props"
              :closable="canUserEditFields"
              :disabled="!canUserEditFields"
              close-icon="close"
              :color="getCategoryColor(item.raw)"
              @click:close="remove(item.raw)"
            >
              {{ item.raw.name }}
            </v-chip>
          </template>
        </v-autocomplete>
      </div>

      <alerts-base
        v-for="error in categoriesErrors"
        :key="error.id"
        type="error"
        class="mb-5"
      >
        <template v-slot:text>
          The '{{ error.name }}' category is not available for the selected merchant on the following
          {{ error.unavailable_dates.length === 1 ? 'date' : 'dates' }}:
          {{ error.unavailable_dates.join('; ') }}{{ error.unavailable_dates_extra_count ? ', and more...' : '.' }}
        </template>
      </alerts-base>

      <div class="form-actions">
        <v-btn
          variant="text"
          class="trigger"
          @click="cancel"
        >
          cancel
        </v-btn>

        <v-btn
          color="primary"
          class="trigger"
          variant="flat"
          :loading="isLoading"
          @click="submit"
        >
          Update Logo Sponsorship
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
// todo merge edit and add new forms
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { debounce, isNonEmptyArr, isNonEmptyStr, isNum, isObj, isStr, isArr } from '@/utils';
import { v1 as uuidv1 } from 'uuid';
import FileUploadArea from '@/components/forms/sponsorships/FileUploadArea.vue';

const businessSearchDebouncer = debounce({
  id: uuidv1(),
  timeout: 500
});

const categorySearchDebouncer = debounce({
  id: uuidv1(),
  timeout: 500
});

const usedCategoryDebouncer = debounce({
  id: uuidv1(),
  timeout: 300
});

// TODO: Translate component

export default {
  components: {
    FileUploadArea,
  },
  setup () {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      businessId: {
        required,
      },
      country: {
        required,
      },
      name: {
        required,
      },
      merchant: {
        required,
      },
      selectedCategories: {
        required,
      },
      startDate: {
        required,
      },
      endDate: {
        required,
      },
      startTime: {
        required,
      },
      endTime: {
        required,
      },
    };
  },
  data() {
    return {
      sponsorship: null,
      businessId: null,
      businessSearch: '',
      isSelectingBusiness: false,
      isLoadingBusinesses: false,
      selectedCategories: [],
      unavailableCategories: [],
      country: '',
      merchant: '',
      startDate: '',
      startTime: '',
      endTime: '',
      hours: this.getHours(),
      endDate: '',
      dateConfig: {
        minDate: 'today',
      },
      endDateConfig: {
        minDate: this.getMinEndDate(),
      },
      name: '',
      isLoadingCategory: false,
      status: null,
      isEditingSponsor : false,
      image: null,
      isTryingSubmit: false,
      isDataExtracted: false,
      isDefaultMerchantImageSet: false,
    };
  },
  computed: {
    categories: {
      get() {
        const storeCategories = isArr(this.$store.getters['pepper/groups'])
          ? this.$store.getters['pepper/groups']
          : [];
        const missing = this.selectedCategories.filter(
          cat => storeCategories.findIndex(c => c.id === cat.id) === -1
        );
        return missing.length ? [...storeCategories, ...missing] : storeCategories;
      },
      set(newCategories) {
        return newCategories;
      },
    },
    logoSponsorships() {
      return this.$store.getters['sponsorships/items'];
    },
    countryErrors() {
      if (this.v$.selectedCategories.$dirty && !this.v$.country.$dirty) {
        return [this.$t('please_select_platform_first')];
      }
      if (this.v$.merchant.$dirty && !this.v$.country.$dirty) {
        return [this.$t('please_select_platform_first')];
      }
      return this.v$.country.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('country_required_error');
        }
        return error.$message;
      });
    },
    isLoading() {
      return this.isEditingSponsor ;
    },
    businesses() {
      const storeBusinesses = this.$store.getters['businesses/items'];
      const preselected = this.sponsorship?.business;
      if (isObj(preselected) && !storeBusinesses.some(b => b.id === preselected.id)) {
        return [...storeBusinesses, preselected];
      }
      return storeBusinesses;
    },
    merchants() {
      const storeMerchants = this.$store.getters['pepper/merchants'];
      const preselected = this.sponsorship?.merchant;
      if (isObj(preselected) && !storeMerchants.some(m => m.id === preselected.id)) {
        return [...storeMerchants, preselected];
      }
      return storeMerchants;
    },
    statuses() {
      return this.$store.getters['sponsorships/statuses'];
    },
    businessIdErrors() {
      return this.v$.businessId.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Business is required';
        }
        return error.$message;
      });
    },
    startDateErrors() {
      return this.v$.startDate.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Start date is required';
        }
        return error.$message;
      });
    },
    endDateErrors() {
      return this.v$.endDate.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'End date is required';
        }
        return error.$message;
      });
    },
    nameErrors() {
      return this.v$.name.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Name is required';
        }
        return error.$message;
      });
    },
    merchantErrors() {
      return this.v$.merchant.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Merchant is required';
        }
        return error.$message;
      });
    },
    selectedCategoriesErrors() {
      return this.v$.selectedCategories.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Category is required';
        }
        return error.$message;
      });
    },
    startTimeErrors() {
      return this.v$.startTime.$errors.map(error => {
        if (error.$validator === 'required') {
          return 'Start time is required';
        }
        return error.$message;
      });
    },
    canUserEditFields() {
      // user can edit fields if the sponsorship is not activated yet
      return this.sponsorship?.activated?.id === 0;
    },
    categoriesErrors() {
      const errors = [];

      this.selectedCategories.forEach(({id, name}) => {
        this.unavailableCategories.findIndex(({external_id, unavailable_dates, unavailable_dates_extra_count}) => {
          if (external_id === id) {
            errors.push({
              unavailable_dates: unavailable_dates.split(',').map(date => this.$dayjs(date).format('MMM D, YYYY')),
              unavailable_dates_extra_count,
              name,
            })
            return true;
          }
          return false;
        })
      });

      return errors;
    },
    isImageSelected() {
      return this.image !== null;
    },
  },
  watch: {
    country(newVal, oldVal) {
      if (isNonEmptyStr(newVal) && newVal !== oldVal) {
        this.getCategory();

        if (!this.isDataExtracted) {
          return;
        }
        this.selectedCategories = [];
        this.debouncedGetUsedCategories();

        if (this.isDefaultMerchantImageSet) {
          this.clearDefaultMerchantImage();
        }
      }
    },
    sponsor(nextSponsor) {
      this.clear();
      this.onReceiveSponsor(nextSponsor);
    },
    businessSearch(nextSearch) {
      if (this.isSelectingBusiness) {
        this.isSelectingBusiness = false;
        return;
      }
      if (isStr(nextSearch)) {
        businessSearchDebouncer(() => this.getBusinesses(nextSearch));
      }
    },
    merchant(newVal) {
      if (isObj(newVal) && isNonEmptyStr(newVal.sponsorship_logo_url) && this.isDataExtracted) {
        this.$refs.uploadArea.setImage(newVal.sponsorship_logo_url);
        this.isDefaultMerchantImageSet = true;
      } else {
        if (this.isDefaultMerchantImageSet) {
          this.clearDefaultMerchantImage();
        }

        if (this.isDataExtracted) {
          this.isDefaultMerchantImageSet = false;
        }
      }

      this.debouncedGetUsedCategories();
    },
    startDate() {
      this.debouncedGetUsedCategories();
    },
    endDate() {
      this.debouncedGetUsedCategories();
    },
  },
  created() {
    // this.clear();
    const { id } = this.$route.params;
    const obj = this.logoSponsorships.find(sponsorship => sponsorship.id === Number(id));

    if (isObj(obj)) {
      this.onReceiveSponsor(obj);
    } else if (id) {
      this.getLogoSponsorship(id).then((res) => {
        this.onReceiveSponsor(res);
      });
    }

    if (this.statuses.length === 0) {
      this.getStatuses();
    }

    this.debouncedGetUsedCategories();
  },
  methods: {
    getMinEndDate() {
      return this.$dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    },
    onChangeStartDate(date) {
      this.startDate = date;
    },
    onChangeEndDate(date) {
      this.endDate = date;
    },
    clearDefaultMerchantImage() {
      if (!this.$refs?.uploadArea) return;
      this.$refs.uploadArea.step = 2;
      this.image = null;
    },
    getCategoryColor(item) {
      if (this.unavailableCategories.findIndex(cat => cat.external_id === item.id) !== -1) {
        return 'red';
      }
      return 'default';
    },
    debouncedGetUsedCategories() {
      usedCategoryDebouncer(() => this.getUsedCategories());
    },
    getLogoSponsorship(id) {
      return this.$store.dispatch('sponsorships/getLogoSponsorship', id);
    },
    remove (item) {
      const index = this.selectedCategories.findIndex(cat => {
        return cat.id === item.id;
      })
      if (index >= 0) this.selectedCategories.splice(index, 1)
    },
    threadSearch(nextVal) {
      if (isStr(nextVal) && nextVal !== '') {
        if (this.isRefreshingThreads) {
          this.isRefreshingThreads = false;
          return false;
        }

        threadSearchDebouncer(() => this.getThread(nextVal));
      }
    },
    categorySearch(nextSearch) {
      if (isStr(nextSearch)) {
        if (this.isRefreshingCategory) {
          this.isRefreshingCategory = false;
          return false;
        }

        categorySearchDebouncer(() => this.getCategory(nextSearch));
      }
    },
    getUsedCategories() {
      if (
        !isNonEmptyStr(this.startDate) ||
        !isNonEmptyStr(this.startTime) ||
        !isNonEmptyStr(this.endDate) ||
        !isNonEmptyStr(this.endTime) ||
        !isNum(this.merchant?.id) ||
        !isNonEmptyStr(this.country)
      ) {
        return;
      }

      const query = {
        start_date: this.startDate + ' ' + this.startTime,
        end_date: this.endDate + ' ' + this.endTime,
        merchant_id: this.merchant.id,
        country: this.country,
        sponsorship_id: this.sponsorship.id,
      };

      return this.$store.dispatch('sponsorships/getUsedCategories', query).then(res => {
        this.unavailableCategories = res;
        return res;
      })
    },
    getCategory(search) {
      if (!isNonEmptyStr(this.country)) {
        this.v$.country.$touch();
        return;
      }
      const query = {
        country: this.country,
        search,
      };

      this.isLoadingCategory = true;

      return this.$store
        .dispatch('pepper/getGroups', query)
        .then(({ groups }) => {
          this.categories = groups;
          this.categories.push(...this.sponsorship.categories)
          this.isLoadingCategory = false;
          return groups;
        })
        .catch((err) => {
          this.isLoadingCategory = false;
          return err;
        });
    },
    getStatuses() {
      return this.$store.dispatch('sponsorships/getStatuses');
    },
    getBusinesses(search = '') {
      const query = {
        status: 1, // only active businesses
        per_page: -1,
        search,
      };

      this.isLoadingBusinesses = true;
      return this.$store
        .dispatch('businesses/getItems', query)
        .then((res) => {
          this.isLoadingBusinesses = false;
          return res;
        })
        .catch((err) => {
          this.isLoadingBusinesses = false;
          return err;
        });
    },
    onUploadFile(file) {
      this.isDefaultMerchantImageSet = false;
      this.image = file.file;
    },
    onReceiveSponsor(nextSponsor) {
      this.sponsorship = nextSponsor;
      if (isObj(nextSponsor) && isNum(nextSponsor.id)) {
        this.extractSponsorData(nextSponsor);
      }
    },
    cancel() {
      this.clear();
      this.$router.push(`/sponsorships`);
    },
    submit() {
      this.v$.$touch();
      this.isTryingSubmit = true;

      if (!this.canSubmitRequest() || this.v$.$invalid) {
        return false;
      }

      this.editSponsorship();
    },
    clear() {
      this.v$.$reset();
      this.name = '';
      this.merchant = null;
      this.status = null;
      this.isEditingSponsor  = false;
      this.businessSearch = '';
      this.$store.dispatch('businesses/clearItems');
      this.$store.dispatch('pepper/clearMerchants');
      this.$store.dispatch('pepper/clearGroups');
    },
    editSponsorship() {
      const {
        name, merchant, status, country, businessId,
        selectedCategories, image
      } = this;

      const sponsor = {
        id: this.sponsorship.id,
        name,
        merchant: {
          id: merchant.id,
          name: merchant.name,
        },
        status: this.sponsorship.activated.id === 0 ? null : status,
        country,
        start_date: this.startDate + ' ' + this.startTime,
        end_date: this.endDate + ' ' + this.endTime,
        business_id: businessId,
        categories: selectedCategories,
        image: image || null,
      };

      this.isEditingSponsor  = true;

      return this.$store.dispatch('sponsorships/editSponsorship', sponsor)
        .then(res => {
          this.clear();
          this.$router.push({ name: 'logo-sponsorships', query: { status: 'success' } });
          return res;
        })
        .finally(() => {
          this.isEditingSponsor  = false;
        });
    },
    canSubmitRequest() {
      const {
        name, merchant, status, isEditingSponsor , country, businessId,
        selectedCategories, startDate, endDate, categoriesErrors,
        isDefaultMerchantImageSet, isImageSelected
      } = this;

      const hasRequiredFields =
        isNonEmptyStr(name) &&
        isNonEmptyStr(country) &&
        isNonEmptyStr(startDate) &&
        isNonEmptyStr(endDate) &&
        isNum(status) &&
        isNum(businessId) &&
        isNum(merchant.id) &&
        !categoriesErrors.length &&
        isNonEmptyArr(selectedCategories) &&
        (isImageSelected || isDefaultMerchantImageSet);

      return hasRequiredFields && !isEditingSponsor ;
    },
    extractSponsorData({name, country, dates, business, merchant, status, categories}) {
      this.isDataExtracted = false;
      let datesArr = dates.split(' - ');
      this.startTime = datesArr[0].split(' ')[1];
      this.endTime = datesArr[1].split(' ')[1];
      datesArr = datesArr.map(date => date.split(' ')[0]);
      this.startDate = datesArr[0];
      this.endDate = datesArr[1];

      this.name = name;
      this.country = country.code;
      this.businessId = business.id;
      this.status = status.id;

      this.merchant = {
        ...merchant,
        id: merchant.external_id,
      };

      categories = categories.map(cat => {
        return {
          id: cat.external_id,
          name: cat.name,
          country: cat.country,
        };
      });

      this.selectedCategories = categories;

      const logo = merchant.sponsorship_logo_url;
      this.setImage(logo);
      this.isDefaultMerchantImageSet = true;

      // delay to allow the watchers to run
      // specifically the date and merchant watchers
      setTimeout(() => {
        this.isDataExtracted = true;
      }, 50);
    },
    setImage(logo) {
      if (!this.$refs?.uploadArea) {
        setTimeout(() => this.setImage(logo), 100);
        return;
      }
      this.$refs.uploadArea.setImage(logo);
    },
    getHours() {
      const { $dayjs } = this;
      const items = [];
      const hours = new Array(24).fill();
      const minutesParts = new Array(3).fill();
      const minutesStep = 15;

      hours.forEach((acc, index) => {
        let minutes = 15;
        items.push($dayjs().hour(index).minute(0).format('HH:mm'));

        minutesParts.forEach(() => {
          items.push($dayjs().hour(index).minute(minutes).format('HH:mm'));
          minutes += minutesStep;
        });
      });

      return items;
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

.sponsorship-form-edit {
  padding: $spacing-xl $spacing-md;
  background-color: map.get($colors, shades, 'white');

  .time-fields {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .v-btn {
      margin: 0 $spacing-sm 0 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
